import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AdminSidebar from '../../components/ui/AdminSidebar';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Icon from '../../components/AppIcon';
import { studioService } from '../../utils/studioService';
import { supabase } from '../../utils/supabase';

const AdminStudioDetails = () => {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const studioIdParam = searchParams.get('studioId');

  const [loading, setLoading] = useState(true);
  const [studios, setStudios] = useState([]);
  const [selectedStudioId, setSelectedStudioId] = useState(studioIdParam || '');
  const [studioDetails, setStudioDetails] = useState(null);
  const [apiKeys, setApiKeys] = useState([]);
  const [domains, setDomains] = useState([]);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (!user || userProfile?.role !== 'admin') {
      navigate('/');
      return;
    }

    loadStudios();
  }, [user, userProfile, navigate]);

  useEffect(() => {
    if (selectedStudioId) {
      loadStudioDetails();
    }
  }, [selectedStudioId]);

  const loadStudios = async () => {
    setLoading(true);
    try {
      const { data, error: studiosError } = await studioService.getAllStudios();
      if (!studiosError && data) {
        setStudios(data);
        // If studioId was in URL, load that studio's details
        if (studioIdParam && data.length > 0) {
          setSelectedStudioId(studioIdParam);
        }
      }
    } catch (err) {
      setError('Failed to load studios');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadStudioDetails = async () => {
    if (!selectedStudioId) return;

    setLoading(true);
    setError(null);

    try {
      // Get studio details
      const { data: studio, error: studioError } = await supabase
        .from('gaming_studios')
        .select('*, user_profiles!gaming_studios_owner_id_fkey(id, email, full_name)')
        .eq('id', selectedStudioId)
        .single();

      if (studioError) throw studioError;
      setStudioDetails(studio);

      // Get API keys
      const { data: keys, error: keysError } = await supabase
        .from('studio_api_keys')
        .select('*')
        .eq('studio_id', selectedStudioId)
        .order('created_at', { ascending: false });

      if (!keysError) {
        setApiKeys(keys || []);
      }

      // Get domains
      const { data: domainList, error: domainsError } = await supabase
        .from('studio_domain_whitelist')
        .select('*')
        .eq('studio_id', selectedStudioId)
        .order('created_at', { ascending: false });

      if (!domainsError) {
        setDomains(domainList || []);
      }
    } catch (err) {
      setError('Failed to load studio details');
      console.error('Error loading studio details:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeApiKey = async (keyId) => {
    if (!confirm('Are you sure you want to revoke this API key?')) return;

    setActionLoading(true);
    try {
      const { error } = await supabase
        .from('studio_api_keys')
        .update({ status: 'revoked' })
        .eq('id', keyId);

      if (error) throw error;
      await loadStudioDetails();
    } catch (err) {
      setError('Failed to revoke API key');
      console.error(err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemoveDomain = async (domainId) => {
    if (!confirm('Are you sure you want to remove this domain?')) return;

    setActionLoading(true);
    try {
      const { error } = await supabase
        .from('studio_domain_whitelist')
        .delete()
        .eq('id', domainId);

      if (error) throw error;
      await loadStudioDetails();
    } catch (err) {
      setError('Failed to remove domain');
      console.error(err);
    } finally {
      setActionLoading(false);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  if (loading && !studioDetails) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AdminSidebar />

      <div className="lg:pl-72">
        <div className="sticky top-0 z-40 bg-background/95 backdrop-blur-md border-b border-border">
          <div className="px-4 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-foreground">Studio Technical Details</h1>
                <p className="text-sm text-muted-foreground mt-1">
                  View and manage API keys, domains, and other technical settings
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                iconName="ArrowLeft"
                onClick={() => navigate('/admin-studio-management')}
              >
                Back to Studios
              </Button>
            </div>
          </div>
        </div>

        <main className="p-4 lg:p-8">
          {error && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-2">
                <Icon name="AlertCircle" size={20} className="text-destructive" />
                <span className="text-sm text-destructive">{error}</span>
              </div>
            </div>
          )}

          {/* Studio Selector */}
          <div className="bg-card border border-border rounded-lg p-6 mb-6">
            <label className="text-sm font-medium text-foreground block mb-2">
              Select Studio
            </label>
            <select
              value={selectedStudioId}
              onChange={(e) => setSelectedStudioId(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-white text-black px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            >
              <option value="">-- Select a studio --</option>
              {studios.map((studio) => (
                <option key={studio.id} value={studio.id}>
                  {studio.name}
                </option>
              ))}
            </select>
          </div>

          {studioDetails && (
            <>
              {/* Studio Info */}
              <div className="bg-card border border-border rounded-lg p-6 mb-6">
                <h3 className="text-lg font-semibold text-foreground mb-4">Studio Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Studio Name:</span>
                    <p className="font-medium text-foreground">{studioDetails.name}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Owner:</span>
                    <p className="font-medium text-foreground">
                      {studioDetails.user_profiles?.full_name || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Owner Email:</span>
                    <p className="font-medium text-foreground">
                      {studioDetails.user_profiles?.email || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Revenue Share:</span>
                    <p className="font-medium text-foreground">
                      {studioDetails.revenue_share_percentage}%
                    </p>
                  </div>
                </div>
              </div>

              {/* API Keys Section */}
              <div className="bg-card border border-border rounded-lg p-6 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-foreground">API Keys</h3>
                  <span className="text-sm text-muted-foreground">
                    {apiKeys.length} key{apiKeys.length !== 1 ? 's' : ''}
                  </span>
                </div>

                {apiKeys.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    No API keys found for this studio
                  </p>
                ) : (
                  <div className="space-y-4">
                    {apiKeys.map((key) => (
                      <div key={key.id} className="border border-border rounded-lg p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <div className="flex items-center gap-3 mb-2">
                              <h4 className="font-medium text-foreground">{key.key_name}</h4>
                              <span
                                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                  key.status === 'active'
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-red-100 text-red-800'
                                }`}
                              >
                                {key.status}
                              </span>
                            </div>
                            <p className="text-xs text-muted-foreground">
                              Created: {new Date(key.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          {key.status === 'active' && (
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => handleRevokeApiKey(key.id)}
                              disabled={actionLoading}
                            >
                              Revoke
                            </Button>
                          )}
                        </div>

                        <div className="bg-muted/30 rounded p-3 mb-2">
                          <div className="flex items-center justify-between">
                            <code className="text-xs font-mono text-foreground break-all">
                              {key.key_prefix}••••••••••••••••••••
                            </code>
                            <span className="text-xs text-muted-foreground ml-2">
                              (Hash stored securely)
                            </span>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                          <div>
                            <span className="text-muted-foreground">Type:</span>
                            <p className="text-foreground">{key.key_type}</p>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Environment:</span>
                            <p className="text-foreground">{key.environment}</p>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Rate Limit:</span>
                            <p className="text-foreground">{key.rate_limit_tier}</p>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Expires:</span>
                            <p className="text-foreground">
                              {key.expires_at
                                ? new Date(key.expires_at).toLocaleDateString()
                                : 'Never'}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Domain Whitelist Section */}
              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-foreground">Domain Whitelist</h3>
                  <span className="text-sm text-muted-foreground">
                    {domains.length} domain{domains.length !== 1 ? 's' : ''}
                  </span>
                </div>

                {domains.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    No whitelisted domains for this studio
                  </p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {domains.map((domain) => (
                      <div key={domain.id} className="border border-border rounded-lg p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-foreground break-all">
                              {domain.domain}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              Added {new Date(domain.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            iconName="Trash2"
                            onClick={() => handleRemoveDomain(domain.id)}
                            disabled={actionLoading}
                            className="text-destructive hover:text-destructive ml-2 flex-shrink-0"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          {!selectedStudioId && !loading && (
            <div className="text-center py-12">
              <Icon name="Building" size={64} className="text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-foreground mb-2">
                Select a Studio
              </h3>
              <p className="text-muted-foreground">
                Choose a studio from the dropdown above to view its technical details
              </p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default AdminStudioDetails;
